import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { SegmentControl } from '@blade/ui-kit'
import { isLoggedInAtom } from '@root/entities/auth'
import { trendingPairsStateAtom } from '@root/features/trending-pairs'
import { useTranslation } from '@root/shared/localization'
import { VirtualList } from '@root/shared/ui-kit'

import { PairSearchItem, PairSearchItemSkeleton } from './search-pair-item'

import type { AsyncStateN } from '@blade/std-lib'
import type { Pair, PairModel } from '@root/entities/pair'
import type { Error } from '@root/shared'

type Tab = 'recent' | 'trending'
type IdleSearchContentProps = {
  recentSearchesState: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    Array<PairModel>
  >
  onPairSelected?: (pair: Pair.PairMinimal) => void
}

export const IdleSearchContent: React.FC<IdleSearchContentProps> = ({
  onPairSelected,
  recentSearchesState,
}) => {
  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>(isLoggedIn ? 'recent' : 'trending')

  const [trendingPairsState] = useAtom(trendingPairsStateAtom)

  const renderList = () => {
    if (tab === 'recent') {
      return (
        <List
          getKey={(it) => it.address}
          emptyText={t('trading.search.recent-searches.empty')}
          renderItem={(it) => (
            <PairSearchItem
              key={it.address}
              pair={it}
              onClick={onPairSelected}
            />
          )}
          renderSkeleton={() => <PairSearchItemSkeleton />}
          state={recentSearchesState}
          skeletonsCount={5}
        />
      )
    }

    return (
      <List
        getKey={(it) => it.address}
        renderItem={(it) => (
          <PairSearchItem key={it.address} pair={it} onClick={onPairSelected} />
        )}
        renderSkeleton={() => <PairSearchItemSkeleton />}
        state={trendingPairsState}
        skeletonsCount={5}
      />
    )
  }

  return (
    <Root>
      <TabsContainer>
        <SegmentControl size="h38" value={tab} onChange={setTab}>
          <SegmentControl.Item value={'recent' satisfies Tab}>
            {t('trading.search.recent-searches')}
          </SegmentControl.Item>
          <SegmentControl.Item value={'trending' satisfies Tab}>
            {t('trading.deluge_trending')}
          </SegmentControl.Item>
        </SegmentControl>
      </TabsContainer>
      {renderList()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 6px;
`

const List = styled(VirtualList<PairModel>)`
  --virtual-list-gap: 4px;
`

const TabsContainer = styled.div`
  display: grid;
  justify-content: center;
`
